import React, { useState, useEffect } from 'react';
import { createFormField } from 'Shared/utils/createFormField';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import * as ReviewsUtils from 'Shared/utils/Reviews.utils';
import { reviewFormTemplateErrorsForQuestion } from '../FeedbackFormSection/utils';

// Components
import FormQuestion from '../../../ThreeSixtyReviewPartials/TakeQuestion/FormQuestion';
import WpAlert from 'Wpds/components/alert/WpAlert';

// Styles
import './FeedbackFromReviewTemplateQuestions.scss';

type Props = {
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  value: string;
  submitError;
  feedbackReviewFormTemplate;
  receiverSlug;
  isPreview?: boolean;
};

const FeedbackFromReviewTemplateQuestions = ({
  onChange,
  value,
  submitError,
  feedbackReviewFormTemplate,
  receiverSlug,
  isPreview = false,
}: Props) => {
  const [activeQuestion, setActiveQuestion] = useState(0);
  const questions = ReviewFormTemplatesHelpers.getQuestions(feedbackReviewFormTemplate);

  const showBackButton = activeQuestion !== 0;
  const showNextButton = activeQuestion !== questions.size - 1;

  const submitErrors = submitError || [];

  const questionKeys = questions.map(question => ReviewFormTemplatesHelpers.getQuestionKey(question));

  const questionsWithSubmissionErrorKeys = questionKeys.filter(questionKey =>
    submitErrors.find(error => error.questionKey === questionKey)
  );

  const firstQuestionWithSubmissionErrorKey = questionKeys.find(questionKey =>
    submitErrors.find(error => error.questionKey === questionKey)
  );

  useEffect(() => {
    if (firstQuestionWithSubmissionErrorKey) {
      const activeQuestionIndex = questionKeys.indexOf(firstQuestionWithSubmissionErrorKey);
      if (activeQuestionIndex) setActiveQuestion(activeQuestionIndex);
    }
  }, [firstQuestionWithSubmissionErrorKey]);

  const handleNextClick = () => {
    setActiveQuestion(activeQuestion + 1);
  };

  const handleBackClick = () => {
    setActiveQuestion(activeQuestion - 1);
  };

  if (!feedbackReviewFormTemplate) return null;

  const baseClassName = 'FeedbackFromReviewTemplateQuestions';

  const existingAnswers = ReviewsUtils.convertAnswersToSnakeCase(value);

  const handleSetAnswers = newAnswer => {
    onChange({ ...existingAnswers, ...newAnswer });
  };

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__questions`}>
        {questions.map((question, index) => {
          const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);
          const errorMessage = reviewFormTemplateErrorsForQuestion({
            question: question.toJS(),
            answers: existingAnswers,
          })
            .map(error => error.error)
            .join(', ');

          return (
            <div
              className={`${baseClassName}__questions__question`}
              key={`custom-feedback-${receiverSlug}-${questionKey}`}
            >
              {errorMessage && questionsWithSubmissionErrorKeys.includes(questionKey) && index === activeQuestion && (
                <div className={`${baseClassName}__questions__question__error`}>
                  <WpAlert type="danger">{errorMessage}</WpAlert>
                </div>
              )}
              <FormQuestion
                receiverSlug={receiverSlug}
                question={question}
                isOpen={index === activeQuestion}
                handleMoveNext={showNextButton && handleNextClick}
                handleMoveBack={showBackButton && handleBackClick}
                answers={existingAnswers}
                formData={{}}
                handleSetAnswers={handleSetAnswers}
                isPreview={isPreview}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default createFormField(FeedbackFromReviewTemplateQuestions);
